import React, { createContext, ReactNode, useEffect, useRef } from 'react';
import {
  createWidgetStore,
  WidgetStoreProps,
  WidgetStoreState,
} from './use-widget-store';
import { StoreApi } from 'zustand';

type WidgetStore = ReturnType<typeof createWidgetStore>;

export const WidgetContext = createContext<WidgetStore | null>(null);

interface WidgetProviderProps extends WidgetStoreProps {
  children: ReactNode;
  onRender: (store: StoreApi<WidgetStoreState>) => void;
  // instanceId is inherited from WidgetStoreProps
}

export const WidgetProvider = ({
  children,
  onRender,
  ...props
}: WidgetProviderProps) => {
  const storeRef = useRef<WidgetStore>(createWidgetStore(props));

  useEffect(() => {
    // Always set the store reference first (required for Widget.action() to work)
    const store = storeRef.current;
    onRender(store);

    // Enable/disable store logging (set to false for production)
    const ENABLE_STORE_LOGGING = false;

    if (!ENABLE_STORE_LOGGING) return;

    // Log all store variables when the store is loaded
    const currentState = store.getState();

    console.log('🚀 [Widget Store] Store loaded with all variables:', {
      timestamp: new Date().toISOString(),
      storeState: {
        // Chat state
        chat: {
          sessionId: currentState.chat.sessionId,
          sessionToken: currentState.chat.sessionToken,
          messages: currentState.chat.messages.map(msg => ({
            id: msg.id,
            sender: msg.sender,
            content:
              msg.content.substring(0, 100) +
              (msg.content.length > 100 ? '...' : ''),
            timestamp: msg.timestamp,
          })),
          messageCount: currentState.chat.messages.length,
          isTyping: currentState.chat.isTyping,
          connectionStatus: currentState.chat.connectionStatus,
          filledQuestionnaireData: currentState.chat.filledQuestionnaireData,
          lastWelcomeBackShown: currentState.chat.lastWelcomeBackShown,
          welcomeBackMessageIndex: currentState.chat.welcomeBackMessageIndex,
        },
        // Interface state
        interface: {
          widgetIsOpen: currentState.interface.widgetIsOpen,
          widgetActiveView: currentState.interface.widgetActiveView,
        },
        // Context state
        context: {
          pageContext: currentState.context.pageContext,
          customContext: currentState.context.customContext,
        },
        // Config state
        config: {
          // Only log config keys to avoid sensitive data
          configKeys: Object.keys(currentState.config || {}),
          hasConfig: !!currentState.config,
        },
      },
    });

    // Subscribe to store changes for debugging
    const unsubscribe = store.subscribe((state, prevState) => {
      console.log('🔄 [Widget Store] State changed:', {
        timestamp: new Date().toISOString(),
        changes: {
          chat: {
            sessionId:
              state.chat.sessionId !== prevState.chat.sessionId
                ? {
                    from: prevState.chat.sessionId,
                    to: state.chat.sessionId,
                  }
                : undefined,
            sessionToken:
              state.chat.sessionToken !== prevState.chat.sessionToken
                ? {
                    from: prevState.chat.sessionToken,
                    to: state.chat.sessionToken,
                  }
                : undefined,
            messageCount:
              state.chat.messages.length !== prevState.chat.messages.length
                ? {
                    from: prevState.chat.messages.length,
                    to: state.chat.messages.length,
                  }
                : undefined,
            connectionStatus:
              state.chat.connectionStatus !== prevState.chat.connectionStatus
                ? {
                    from: prevState.chat.connectionStatus,
                    to: state.chat.connectionStatus,
                  }
                : undefined,
            isTyping:
              state.chat.isTyping !== prevState.chat.isTyping
                ? {
                    from: prevState.chat.isTyping,
                    to: state.chat.isTyping,
                  }
                : undefined,
            filledQuestionnaireData:
              JSON.stringify(state.chat.filledQuestionnaireData) !==
              JSON.stringify(prevState.chat.filledQuestionnaireData)
                ? {
                    from: prevState.chat.filledQuestionnaireData,
                    to: state.chat.filledQuestionnaireData,
                  }
                : undefined,
          },
          interface: {
            widgetIsOpen:
              state.interface.widgetIsOpen !== prevState.interface.widgetIsOpen
                ? {
                    from: prevState.interface.widgetIsOpen,
                    to: state.interface.widgetIsOpen,
                  }
                : undefined,
            widgetActiveView:
              state.interface.widgetActiveView !==
              prevState.interface.widgetActiveView
                ? {
                    from: prevState.interface.widgetActiveView,
                    to: state.interface.widgetActiveView,
                  }
                : undefined,
          },
        },
      });
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Set isInlineMode based on config.ui.target
    storeRef.current
      .getState()
      .interface.setIsInlineMode(!!props.config.ui?.target);
  }, []);
  return (
    <WidgetContext.Provider value={storeRef.current}>
      {children}
    </WidgetContext.Provider>
  );
};
